import {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { autorun } from 'mobx';

import useComponentMounted from '../../chat/hooks/useComponentMounted';
import ClientDocument, { DOCUMENTS_PAGE_SIZE } from '../../chat/Model/ClientDocument';

const useClientDocuments = ({
  userId, channelId, onlySaved = false, coachId = null,
}) => {
  const [collectionList, setCollectionList] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const isMountedRef = useComponentMounted();

  useEffect(() => {
    if (!userId) {
      setDocuments([]);
      setCollectionList([]);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    setIsLoading(true);
    setCollectionList([]);
    setHasMore(false);

    const init = async () => {
      const initialCollection = ClientDocument.getDocuments({
        userId, channelId, onlySaved, coachId,
      });
      await initialCollection.fetch();

      if (!cancelled && isMountedRef.current) {
        setCollectionList([initialCollection]);
        setHasMore(initialCollection.docs.length === DOCUMENTS_PAGE_SIZE);
        setIsLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, [userId, channelId, onlySaved, coachId, isMountedRef]);

  useEffect(() => {
    if (!collectionList.length) return undefined;

    const disposer = autorun(() => {
      const seen = new Set();
      const docs = collectionList
        .flatMap((collection) => collection.docs)
        .filter((doc) => {
          if (seen.has(doc.id)) return false;
          seen.add(doc.id);
          return true;
        });
      setDocuments(docs);
    });

    return () => disposer();
  }, [collectionList]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !collectionList.length) return;

    setIsLoadingMore(true);

    const lastCollection = collectionList[collectionList.length - 1];
    const lastDoc = lastCollection.docs[lastCollection.docs.length - 1];

    const nextCollection = ClientDocument.getDocuments({
      userId,
      channelId,
      onlySaved,
      coachId,
      startAfterDoc: lastDoc,
    });

    try {
      await nextCollection.fetch();

      if (isMountedRef.current) {
        if (nextCollection.docs.length < DOCUMENTS_PAGE_SIZE) setHasMore(false);
        setCollectionList((prev) => [...prev, nextCollection]);
      }
    } finally {
      if (isMountedRef.current) setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, collectionList, userId, channelId, onlySaved, coachId, isMountedRef]);

  return {
    documents,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    pagesLoaded: collectionList.length,
  };
};

export default useClientDocuments;
